import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiUsers, FiCalendar, FiBarChart2, FiAlertCircle, FiPackage } from 'react-icons/fi';
import Layout from '../components/Layout';
import ErrorBoundary from '../components/ErrorBoundary';
import SupermanPaymentAnalytics from '../components/SupermanPaymentAnalytics';
import SupermanMemberAnalytics from '../components/SupermanMemberAnalytics';

const formatAmount = (amount) => new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
}).format(amount || 0);

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-SG');
};

const SupermanDashboardContent = () => {
    const [stats, setStats] = useState({
        totalMembers: 0,
        totalPackages: 0,
        activePackages: 0,
        completedPackages: 0,
        cancelledPackages: 0,
        upcomingReviews: [],
        thisMonth: {
            month: '',
            totalPaidMember: 0,
            totalSGDAmount: 0,
            reviewsDone: 0,
        },
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/supermanpayments/dashboard-stats`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                            'ngrok-skip-browser-warning': 'true',
                        },
                    }
                );
                if (response.data.success) {
                    setStats(response.data.data);
                }
            } catch (err) {
                setError(`Failed to fetch statistics: ${err.response?.data?.error || err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6 w-full max-w-none px-8">
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-6">Superman Dashboard Overview</h2>

                {error && (
                    <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md flex items-center">
                        <FiAlertCircle className="mr-2" />
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <Link to="/superman/members" className="bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors">
                        <div className="flex items-center">
                            <FiUsers className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-blue-800">Superman Members</h3>
                                <p className="text-3xl font-bold text-blue-600">{stats.totalMembers}</p>
                            </div>
                        </div>
                    </Link>

                    <Link to="/superman/payments" className="bg-purple-50 p-4 rounded-lg hover:bg-purple-100 transition-colors">
                        <div className="flex items-center">
                            <FiPackage className="h-8 w-8 text-purple-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-purple-800">Review Packages</h3>
                                <p className="text-3xl font-bold text-purple-600">{stats.totalPackages}</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-purple-700">Active</span>
                                <span className="font-medium text-purple-800">{stats.activePackages}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-green-700">Completed</span>
                                <span className="font-medium text-green-800">{stats.completedPackages}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-red-700">Cancelled</span>
                                <span className="font-medium text-red-800">{stats.cancelledPackages}</span>
                            </div>
                        </div>
                    </Link>

                    <div className="bg-amber-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiCalendar className="h-8 w-8 text-amber-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-amber-800">Upcoming Reviews</h3>
                                <p className="text-sm text-amber-700">Next 14 days</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            {stats.upcomingReviews?.length > 0 ? (
                                stats.upcomingReviews.map((row) => (
                                    <div key={row._id} className="flex flex-col text-sm border-b border-amber-200 pb-2">
                                        <div className="font-medium text-amber-900">{row.memberLabel}</div>
                                        <div className="text-amber-700 text-xs">
                                            Due: {formatDate(row.nextReviewDate)} · bal {row.reviewBal}/{row.reviewCount}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-sm text-amber-700">No upcoming reviews</div>
                            )}
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg md:col-span-2 lg:col-span-3">
                        <div className="flex items-center">
                            <FiBarChart2 className="h-8 w-8 text-yellow-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-yellow-800">This Month Analytics</h3>
                                <p className="text-sm font-bold text-yellow-600">{stats.thisMonth?.month}</p>
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="flex justify-between sm:flex-col text-sm">
                                <span className="text-yellow-700">Paid Members</span>
                                <span className="font-medium text-yellow-800">{stats.thisMonth?.totalPaidMember || 0}</span>
                            </div>
                            <div className="flex justify-between sm:flex-col text-sm">
                                <span className="text-yellow-700">Revenue</span>
                                <span className="font-medium text-yellow-800">
                                    {formatAmount(stats.thisMonth?.totalSGDAmount)}
                                </span>
                            </div>
                            <div className="flex justify-between sm:flex-col text-sm">
                                <span className="text-yellow-700">Reviews Done</span>
                                <span className="font-medium text-yellow-800">{stats.thisMonth?.reviewsDone || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <SupermanPaymentAnalytics />

            <div className="pb-8">
                <SupermanMemberAnalytics />
            </div>
        </div>
    );
};

const SupermanDashboard = () => (
    <Layout>
        <ErrorBoundary
            fallbackTitle="Superman Dashboard Error"
            fallbackDescription="An error occurred while loading the Superman dashboard. Please try again or contact support if the problem persists."
        >
            <SupermanDashboardContent />
        </ErrorBoundary>
    </Layout>
);

export default SupermanDashboard;
